import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import { formatDate } from '../../../utils/buyerInsightsDates';
import TimelineEvents from './TimelineEvents';
import TimelineLanes from './TimelineLanes';

const cache = new Map(); // `${id}|${start}|${end}|${store}` → data

// One product's timeline (spec §3). Fetched on first open, cached per product + window. Wide:
// lanes then the event list. Narrow: the event list is the rendering; the chart is behind a toggle
// and scrolls sideways.
const ProductTimeline = ({ productId, view, window, myRetailerId, isWide }) => {
  const key = `${productId}|${window.start}|${window.end}|${myRetailerId || ''}`;
  const [data, setData] = useState(() => cache.get(key) || null);
  const [error, setError] = useState('');
  const [showChart, setShowChart] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    track('Buyer Insights Timeline Opened', { tab: 'products', view, product_id: productId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cached = cache.get(key);
    if (cached) {
      setData(cached);
      return undefined;
    }
    seq.current += 1;
    const mine = seq.current;
    setData(null);
    setError('');
    const params = { start: window.start, end: window.end };
    if (myRetailerId) params.my_retailer_id = myRetailerId;
    dashboardAPI
      .getBuyerInsightsProductTimeline(productId, params)
      .then((res) => {
        if (mine !== seq.current) return;
        if (res && res.success) {
          cache.set(key, res.data);
          setData(res.data);
        } else {
          setError((res && res.error) || 'Could not load the timeline');
        }
      })
      .catch((e) => mine === seq.current && setError(e.message || 'Could not load the timeline'));
    return undefined;
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Alert severity="error" variant="outlined" sx={{ my: 1 }}>{error}</Alert>;
  if (!data) return <Skeleton variant="rectangular" height={isWide ? 220 : 160} sx={{ borderRadius: 1, my: 1 }} />;

  const subtitle = `${formatDate(data.window.start)} – ${formatDate(data.window.end)} · one day per column · ${
    data.partners === 1 ? '1 partner carries this product' : `${data.partners} partners carry this product`
  }`;

  return (
    <Box sx={{ my: 1 }} data-testid="product-timeline">
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {subtitle}
      </Typography>
      {isWide ? (
        <>
          <TimelineLanes data={data} />
          <Box sx={{ mt: 2 }}>
            <TimelineEvents events={data.events} />
          </Box>
        </>
      ) : (
        <>
          <TimelineEvents events={data.events} />
          <Button size="small" onClick={() => setShowChart((v) => !v)} sx={{ mt: 1, minHeight: 44 }}>
            {showChart ? 'Hide chart' : 'Show chart'}
          </Button>
          {showChart && (
            <Box sx={{ overflowX: 'auto', mt: 1 }}>
              <TimelineLanes data={data} minWidth={640} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ProductTimeline;
